import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apifetch } from '../services/interceptors';
import MandatoryMfaSetup from './MandatoryMfaSetup';
import { isStaffPortalPath } from '../utils/staffPaths';
import MfaPromoBanner, { isMfaPromoBannerDismissed, dismissMfaPromoBanner } from './MfaPromoBanner';

// Paths that never require a tenant-user token / shouldn't be gated or bannered (public
// pages, admin pages which use a separate Admintoken, auth pages themselves).
const EXCLUDED_PREFIXES = [
  '/', '/home', '/login', '/desktop-login', '/register', '/forgetpass', '/downloads', '/report', '/terms',
  '/admin', '/scan/', '/scan-links', '/download/', '/widget', '/external-risk-report'
];

export function isExcluded(pathname) {
  return isStaffPortalPath(pathname) || EXCLUDED_PREFIXES.some(p => pathname === p || (p !== '/' && pathname.startsWith(p)));
}

// Wraps the authenticated app: (1) blocks access behind a mandatory authenticator-setup
// screen for accounts whose default MFA method (totp) hasn't been enrolled yet, and
// (2) shows a dismissible promo banner nudging existing email-code users to switch.
const MfaGate = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null); // { method, totp_enabled } | null while unknown
  const [bannerDismissed, setBannerDismissed] = useState(isMfaPromoBannerDismissed());

  const excluded = isExcluded(location.pathname);

  useEffect(() => {
    if (excluded) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    apifetch('s1/api/mfa/status', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (!cancelled && data) setStatus(data); })
      .catch(() => {});
    return () => { cancelled = true; };
    // Re-check on route changes so a just-completed login/registration picks up state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, excluded]);

  if (!excluded && status && status.method === 'totp' && !status.totp_enabled) {
    return (
      <MandatoryMfaSetup
        // method is 'totp' (just finished enrollment) or 'email' (explicitly opted in
        // instead) — either way the required choice has been made, so unblock the app.
        onComplete={(method) => setStatus(prev => ({
          method: method || prev?.method || 'email',
          totp_enabled: method === 'totp' ? true : !!prev?.totp_enabled
        }))}
      />
    );
  }

  const showBanner = !excluded && status && status.method === 'email' && !bannerDismissed;

  return (
    <>
      {showBanner && (
        <MfaPromoBanner
          onSetUp={() => navigate('/dashboard?tab=Profile')}
          onDismiss={() => { dismissMfaPromoBanner(); setBannerDismissed(true); }}
        />
      )}
      {children}
    </>
  );
};

export default MfaGate;
